export type TierIISnapshotMaxMode = 'highest' | 'total_over_period' | 'highest_plus_safety';
export type TierIISnapshotAvgMode = 'average_readings' | 'total_over_days' | 'percent_of_max';

/** Aggregated usage supplies the total; derivation options carry its entry count and highest entry. */
export function tierIIInventorySourceAmounts(
  source: string | null,
  snapshotAmounts: readonly number[],
  annualUsageLbs: number | null,
): readonly number[] {
  if (source !== 'usage_records') return snapshotAmounts;
  return annualUsageLbs == null ? [] : [annualUsageLbs];
}

export interface TierIISnapshotDerivationOptions {
  maxMode: TierIISnapshotMaxMode | null;
  avgMode: TierIISnapshotAvgMode | null;
  safetyPct: number | null;
  backlogPeriods: number | null;
  avgDays: number | null;
  avgPercentOfMax: number | null;
  /** Number of original usage entries when readings contain an aggregated annual total. */
  averageEntryCount?: number | undefined;
  /** Highest original usage entry when readings contain an aggregated total. */
  highestEntryAmount?: number | null | undefined;
}

/**
 * Derive reportable maximum and average pounds from inventory readings.
 * Shared by the client preview and every server-side reporting path.
 */
export function tierIISnapshotDerivedAmounts(
  readingsLbs: readonly number[],
  opts: TierIISnapshotDerivationOptions,
): { maxLbs: number | null; avgLbs: number | null } {
  const readings = readingsLbs.filter((amount) => Number.isFinite(amount) && amount >= 0);
  if (readings.length === 0) return { maxLbs: null, avgLbs: null };

  const total = readings.reduce((sum, amount) => sum + amount, 0);
  const highest = opts.highestEntryAmount === undefined ? Math.max(...readings) : opts.highestEntryAmount;

  let maxLbs: number | null;
  switch (opts.maxMode ?? 'highest') {
    case 'total_over_period':
      maxLbs = opts.backlogPeriods != null && opts.backlogPeriods > 0 ? total / opts.backlogPeriods : null;
      break;
    case 'highest_plus_safety':
      maxLbs = highest == null ? null : highest * (1 + (opts.safetyPct ?? 0) / 100);
      break;
    case 'highest':
      maxLbs = highest;
      break;
  }

  let avgLbs: number | null;
  switch (opts.avgMode ?? 'average_readings') {
    case 'total_over_days':
      avgLbs = opts.avgDays != null && opts.avgDays > 0 ? total / opts.avgDays : null;
      break;
    case 'percent_of_max':
      avgLbs =
        maxLbs != null && opts.avgPercentOfMax != null && opts.avgPercentOfMax >= 0
          ? maxLbs * (opts.avgPercentOfMax / 100)
          : null;
      break;
    case 'average_readings':
      {
        const count = opts.averageEntryCount ?? readings.length;
        avgLbs = Number.isInteger(count) && count > 0 ? total / count : null;
      }
      break;
  }

  return {
    maxLbs: maxLbs == null ? null : Math.round(maxLbs),
    avgLbs: avgLbs == null ? null : Math.round(avgLbs),
  };
}
